// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SystemBuilderMiniSplitSpecificationView from "@plumberssupply/App/Components/SystemBuilder/SystemBuilderMiniSplitSpecificationView";
import SystemBuilderTypesView from "@plumberssupply/App/Components/SystemBuilder/SystemBuilderTypesView";
import SystemBuilderUnitarySpecificationView from "@plumberssupply/App/Components/SystemBuilder/SystemBuilderUnitarySpecificationView";
import SystemBuilderYourSystemView from "@plumberssupply/App/Components/SystemBuilder/SystemBuilderYourSystemView";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { SystemBuilderKey } from "@plumberssupply/XDL/constants";
import { xClassNames } from "@plumberssupply/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

const enum fields {
    unitaryImage = "unitaryImage",
    miniSplitImage = "miniSplitImage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.unitaryImage]: string;
        [fields.miniSplitImage]: string;
    };
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        step,
        systemType,
        productsInfo,
        unitaryOutputsystems,
        unitaryConfigurations,
        miniSplitOutputsystems,
        miniSplitConfugurations,
    } = state.xSystemBuilder;

    const savedSystemType = JSON.parse(window.localStorage.getItem(SystemBuilderKey) || "{}").type;

    // Use saved type if available to check systems otherwise fall back to state type
    const type = savedSystemType || systemType;

    const systems = type === "unitary" ? unitaryOutputsystems : miniSplitOutputsystems;
    const isClearSpecBtnDisabled =
        systemType === "unitary" ? !unitaryConfigurations.selectedBrand : !miniSplitConfugurations.selectedBrand;
    return {
        currentStep: step,
        systemType,
        productsInfo,
        systems,
        isClearSpecBtnDisabled,
    };
};

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SystemBuilderStyles {
    wrapper?: InjectableCss;
    topSection?: InjectableCss;
    title?: TypographyPresentationProps;
    subTitleWrapper?: InjectableCss;
    subTitle?: TypographyPresentationProps;
    clearSpecButton?: ButtonPresentationProps;
    filterTogglesWrapper?: InjectableCss;
    previous?: LinkPresentationProps;
    stepperWrapper?: InjectableCss;
    stepperItem: {
        wrapper?: InjectableCss;
        circle?: InjectableCss;
        circleIcon?: IconPresentationProps;
        title?: TypographyProps;
    };
}

export const systemBuilderStyles: SystemBuilderStyles = {
    stepperItem: {},
};

const styles = systemBuilderStyles;

const steps = [
    {
        name: "Type",
        index: 0,
        next: 1,
    },
    {
        name: "Specifications",
        prev: 0,
        index: 1,
        next: 2,
    },
    {
        name: "Your System",
        prev: 1,
        index: 2,
    },
];

const SystemBuilder: FC<Props> = ({
    fields,
    currentStep,
    systemType,
    productsInfo,
    systems,
    isClearSpecBtnDisabled,
}) => {
    const dispatch = useDispatch();
    const [filters, setFilters] = useState({ inStock: false, isRebate: false });
    const { hasResults, step, type } = JSON.parse(window.localStorage.getItem(SystemBuilderKey) || "{}");

    useEffect(() => {
        setFilters({ inStock: false, isRebate: false });
    }, [systems]);

    // updated step and type from localStorage if we have prev results saved
    useEffect(() => {
        if (hasResults) {
            dispatch({
                type: "xSystemBuilder/SetStep",
                step,
                systemType: type,
            } as any);
        }
    }, []);

    useEffect(() => {
        // we need to clear any saved data in first step and when no results are saved
        if (currentStep === 0 && !hasResults) {
            dispatch({
                type: "xSystemBuilder/ClearAllSystems",
            } as any);
        }
    }, [currentStep]);

    const goToPreviousStep = () => {
        dispatch({
            type: "xSystemBuilder/SetStep",
            step: currentStep - 1,
        } as any);
    };

    const moveToStepHandler = (newStep: number) => {
        dispatch({
            type: "xSystemBuilder/SetStep",
            step: newStep,
        } as any);
    };

    const clearSpecificationsHandler = () => {
        localStorage.removeItem(SystemBuilderKey);
        dispatch({
            type:
                systemType === "unitary"
                    ? "xSystemBuilder/ClearUnitarySystemObject"
                    : "xSystemBuilder/ClearMiniSplitConfigurationObject",
        });
    };

    const handleToggleChange = (key: keyof typeof filters, _: React.SyntheticEvent<Element, Event>, value: boolean) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);

        let result = systems;

        if (newFilters.inStock) {
            result = result.filter(system =>
                system.products.every(product => {
                    const info = productsInfo?.[product.id];
                    const availability = info?.inventory?.inventoryAvailabilityDtos?.find(
                        dto => dto?.unitOfMeasure?.toLowerCase() === info?.unitOfMeasure?.toLowerCase(),
                    )?.availability;

                    return availability?.messageType === 1;
                }),
            );
        }

        if (newFilters.isRebate) {
            result = result.filter(system => system.rebateEligible);
        }

        dispatch({
            type: "xSystemBuilder/SetSystems",
            systems,
            filteredSystems: result,
            hasFilters: newFilters.isRebate || newFilters.inStock,
        } as any);
    };

    if (hasResults && !systems.length && currentStep === 0) {
        return null;
    }

    const StepperComponent = () => {
        const style = styles.stepperItem;
        const maxStep = 2;
        return (
            <Typography as="ul" {...styles.stepperWrapper}>
                {steps.map((item, index) => {
                    const isCompleted = item.index < currentStep;
                    const isActive = item.index === currentStep;
                    const isClickable = item.index <= currentStep;

                    const props = { isCompleted, isActive, isClickable };

                    const classNames = xClassNames.append([
                        isActive && "active",
                        isCompleted && "completed",
                        isClickable && "clickable",
                    ]);

                    return (
                        <Typography
                            as="li"
                            {...style.wrapper}
                            {...props}
                            key={item.name}
                            className={classNames}
                            aria-disabled={!isClickable && item.index !== 0}
                            onClick={() => isClickable && moveToStepHandler(item.index)}
                        >
                            <Typography {...style.circle} {...props}>
                                {isCompleted ? <Icon {...style.circleIcon} /> : index + 1}
                            </Typography>
                            <Typography {...style.title} {...props}>
                                {item.name}
                            </Typography>
                        </Typography>
                    );
                })}
            </Typography>
        );
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.topSection}>
                <Typography {...styles.title}>{translate("System Builder")}</Typography>
                <StepperComponent />
            </StyledWrapper>
            <StyledWrapper>
                {currentStep > 0 && (
                    <Link {...styles.previous} onClick={goToPreviousStep}>
                        {translate("Previous Step")}
                    </Link>
                )}

                <StyledWrapper {...styles.subTitleWrapper}>
                    <Typography {...styles.subTitle}>{`${translate("Select")} ${steps[currentStep].name}`}</Typography>
                    {currentStep === 1 && (
                        <Button
                            {...styles.clearSpecButton}
                            onClick={clearSpecificationsHandler}
                            disabled={isClearSpecBtnDisabled}
                        >
                            {translate("Clear All Selections")}
                        </Button>
                    )}
                </StyledWrapper>
                {currentStep === 2 && (
                    <StyledWrapper {...styles.filterTogglesWrapper}>
                        <Checkbox
                            variant="toggle"
                            checked={filters.inStock}
                            onChange={(e, value) => handleToggleChange("inStock", e, value)}
                        >
                            {translate("In Stock")}
                        </Checkbox>
                        <Checkbox
                            variant="toggle"
                            checked={filters.isRebate}
                            onChange={(e, value) => handleToggleChange("isRebate", e, value)}
                        >
                            {translate("Is Rebate")}
                        </Checkbox>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            {(() => {
                switch (currentStep) {
                    case 0:
                        return (
                            <SystemBuilderTypesView
                                unitaryImageSrc={fields.unitaryImage}
                                miniSplitImageSrc={fields.miniSplitImage}
                            />
                        );
                    case 1:
                        return (
                            <>
                                {systemType === "unitary" ? (
                                    <SystemBuilderUnitarySpecificationView />
                                ) : (
                                    <SystemBuilderMiniSplitSpecificationView />
                                )}
                            </>
                        );
                    case 2:
                        return <SystemBuilderYourSystemView />;
                    default:
                        return null;
                }
            })()}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SystemBuilder),
    definition: {
        group: "Xngage Custom" as any,
        displayName: "System Builder",
        fieldDefinitions: [
            {
                name: fields.unitaryImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.miniSplitImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
